import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.tsx'
import { LanguageProvider } from './i18n'
import { ThemeProvider } from './theme'
import { AuthProvider } from './auth'
import { initGA, trackPageView } from './analytics'
import { initFirebase } from './firebase'

// Sentry 환경 이름 (소문자로 정규화)
const SENTRY_ENV = (import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development').toLowerCase()

// Sentry 초기화 (가장 먼저 실행되어야 함)
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: SENTRY_ENV, // 'local', 'staging', 'production'
    release: `releasegate@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,
    // 기본 PII 데이터 (예: IP 주소) 전송 허용
    sendDefaultPii: true,
    // 트레이싱 샘플 레이트 (성능 모니터링) - production에서는 낮추는 것 권장
    tracesSampleRate: SENTRY_ENV === 'production' ? 0.2 : 1.0,
    // 세션 리플레이 샘플 레이트
    replaysSessionSampleRate: SENTRY_ENV === 'production' ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // 개발 환경에서는 콘솔에도 로깅
    debug: SENTRY_ENV === 'development' || SENTRY_ENV === 'local',
    // 무시할 에러 (브라우저 노이즈)
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      /Loading chunk \d+ failed/,
      /ChunkLoadError/,
    ],
    // 에러 필터링
    beforeSend(event, hint) {
      const error = hint.originalException;
      // 사용자 취소 에러는 무시
      if (error instanceof Error && error.message === 'User cancelled') {
        return null;
      }
      return event;
    },
  })
}

// GA4 초기화
initGA()
trackPageView('/', 'Release Decision Board')

// Firebase 초기화
initFirebase()

// Sentry ErrorBoundary 폴백 컴포넌트
function ErrorFallback() {
  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      padding: '20px',
      background: '#0a0a0f',
      color: '#fff',
      fontFamily: 'system-ui, sans-serif',
    }}>
      <div style={{ textAlign: 'center', maxWidth: '400px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
          문제가 발생했습니다
        </h1>
        <p style={{ color: '#888', marginBottom: '24px' }}>
          예상치 못한 오류가 발생했습니다. 페이지를 새로고침 해주세요.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            background: '#00ff88',
            color: '#000',
            border: 'none',
            padding: '12px 24px',
            fontWeight: 'bold',
            cursor: 'pointer',
          }}
        >
          페이지 새로고침
        </button>
      </div>
    </div>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <App />
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
